import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { BiEdit } from "react-icons/bi";

import { Plus, Search } from "lucide-react";
import { useLabPartnerListAPI } from "@/app/hooks/api_hooks/LabReport/useLabPartnerListAPI";
import clsx from "clsx";
import { PRIMARY_BUTTON } from "@/constants";

const LabPartnerListComponent = () => {
  const { NO_PWL_RESULTS } = useImageConstants();

  const { execute: fetchLabPartners, labList } = useLabPartnerListAPI();

  const navigate = useNavigate();

  useEffect(() => {
    fetchLabPartners();
  }, []);

  const handelProductEdit = (id: string) => {
    navigate(`/app/clinic-lab-report/lab-partner-onboard?lab_id=${id}&page_step=ONBOARDING`);
  };
  const AddNewLabBranch = () => {
    navigate(`/app/clinic-lab-report/lab-partner-onboard?page_step=ONBOARDING`);
  };

  const isLabPartnerListInProgess = useLoader("isLabPartnerListInProgress");

  const [searchQuery, setSearchQuery] = useState("");

  const filteredLabList = labList?.filter((item) =>
    item?.branch_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item?.lab_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <div className="bg-grey-100 p-2 h-full">
        <div className="bg-white px-3 py-3 rounded-xl shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold text-base text-[#000000] ">Branches</h2>
            </div>

            <button onClick={AddNewLabBranch} className={PRIMARY_BUTTON}>
              <Plus className="w-4 h-4" /> Add Branch
            </button>
          </div>
        </div>

        <div className="bg-white mt-3  rounded-xl shadow-sm px-3 py-1">
          {/* <div className="mt-2 relative w-full max-w-md">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-gray-500 pointer-events-none">
              <MdSearch className="text-black" />
            </span>

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Branch"
              className="w-full pl-12 pr-4 py-2 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-gray-200 outline-none"
            />
          </div> */}

          <div className="mt-2 relative w-full max-w-md mb-4 bg-white">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-gray-500 pointer-events-none">
              <Search className="text-gray-400 w-5 h-5" />
            </span>

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Branch Name or Lab Name"
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-gray-100 outline-none transition-all text-sm"
            />
          </div>

          {isLabPartnerListInProgess ? (
            <TableSkeleton />
          ) : labList?.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_PWL_RESULTS}
              message="No Branch Listed"
              description="Please add a branch to get started."
            />
          ) : filteredLabList?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 bg-white rounded-xl">
              <p className="text-gray-500 font-medium">No results found for "{searchQuery}"</p>
              <p className="text-gray-400 text-sm mt-1">Try searching with a different name</p>
            </div>
          ) : (
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <table className="w-full min-w-[820px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                        <tr>
                          {["Sr. No", "Branch ID", "Branch Name", "Lab Name", "Location", "Status", "Actions"].map(
                            (col) => (
                              <th
                                key={col}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {col}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      <tbody className="overflow-hidden rounded-xl w-full">
                        {filteredLabList?.map((item, index) => {
                          const isFirst = index === 0;
                          const isLast = index === filteredLabList?.length - 1;
                          const isSingle = filteredLabList?.length === 1;
                          return (
                            <tr
                              key={item?.branch_id}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23] align-middle",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                {index + 1}
                              </td>

                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                <p className="text-xs bg-blue-50 p-1 inline-block rounded-md px-2">
                                  {item?.branch_code}
                                </p>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#1F1F23]">
                                {item?.display_name || "-"}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {item?.lab_name || "-"}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {item?.address_details?.city}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                <span
                                  className={clsx(
                                    "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium capitalize",
                                    item?.status === "active"
                                      ? "bg-[#F6FFF6] text-[#00873C]"
                                      : "bg-[#FFF4F4] text-[#ED0101]",
                                  )}
                                >
                                  <span className="w-2 h-2 rounded-full bg-current"></span>
                                  {item?.status}
                                </span>
                              </td>

                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                <button
                                  onClick={() => handelProductEdit(item?.lab_id)}
                                  className="inline-flex h-7 w-7 items-center justify-center rounded-xl bg-slate-100 text-[#1F1F23] transition hover:bg-slate-200"
                                >
                                  <BiEdit className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LabPartnerListComponent;
